import { useBoxSlotSidebar } from "@modules/boxSlot/hooks/useBoxSlotSidebar"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ProgramSessionFormView } from "@modules/programSession/components/programSessionFormView"
import { ChronoDetail } from "@modules/sessionBlock/exercises/components/chronoDetail"
import { ExerciseFormInfos } from "@modules/sessionBlock/exercises/components/exercisesForms"
import { MediaForm } from "@modules/sessionBlock/media/components/mediaForm"
import { NoteForAthletesForm } from "@modules/sessionBlock/notes/components/noteForAthletesForm"
import { NoteForCoachesForm } from "@modules/sessionBlock/notes/components/noteForCoachesForm"
import { SessionBlockFormikType } from "@modules/sessionBlock/sessionBlockFormikTypes"
import {
  DetailedSessionType,
  ProgramSessionDetailsComponent,
} from "@modules/sessions/components/programSessionDetailsComponent"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { SidebarNavigatorParams } from "@modules/sidebar/sidebarTypes"
import { useEffect } from "react"

export const useSessionSidebar = () => {
  const { sidebarNavigateTo, hideSidebar, isSidebarVisible, sidebarNavigateBack } = useSidebar()
  const { navigateToBoxSlotView } = useBoxSlotSidebar()
  const { boxSlotService } = useServicesContext()
  const {
    setInitialUpdatedBlock,
    setUpdatedBlockIndex,
    setIsEditingBlock,
    onHideSessionForm,
    onHideBlockForm,
    onHideChronoForm,
    setEditedSession,
    setEditedSlot,
    setDateSession,
    resetForm,
  } = useSessionFormContext()

  useEffect(() => {
    if (!isSidebarVisible) {
      resetForm()
    }
  }, [isSidebarVisible])

  const navigateToSessionView = (
    session: DetailedSessionType,
    refreshCalendarEvents?: () => void,
    onHide?: () => Promise<boolean>,
  ) => {
    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    return sidebarNavigateTo({
      content: (
        <ProgramSessionDetailsComponent
          editedSession={session}
          allowEditContent={true}
          refreshTraining={refreshCalendarEvents}
        />
      ),
      replaceAllComponents: true,
      onHide,
    })
  }

  const navigateToSessionForm = (
    isCreation: boolean = true,
    replaceAllComponents: boolean = true,
    refreshCalendarEvents?: () => void,
    slotId?: string,
    args?: SidebarNavigatorParams,
  ) => {
    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    return sidebarNavigateTo({
      content: (
        <ProgramSessionFormView
          isCreation={isCreation}
          onFinish={() => {
            setEditedSession(undefined)
            setDateSession(undefined)
            hideSidebar(false)
            setEditedSlot(undefined)
            refreshCalendarEvents && refreshCalendarEvents()
            boxSlotService.updateBoxSlotsBeingViewed([])
          }}
        />
      ),
      leftButtonText: word("global.return"),
      leftButtonAction: () => {
        if (slotId && refreshCalendarEvents) {
          navigateToBoxSlotView(slotId, refreshCalendarEvents, "session")
          return Promise.resolve(false)
        } else return Promise.resolve(true)
      },
      replaceAllComponents,
      onHide: () => {
        return onHideSessionForm.current()
      },
      ...args,
    })
  }

  const navigateToExerciseForm = (
    isCreation: boolean,
    updatedExerciseIndex: number | null,
    initialUpdatedExercise?: SessionBlockFormikType,
  ) => {
    setIsEditingBlock(!isCreation)
    if (initialUpdatedExercise) setInitialUpdatedBlock(initialUpdatedExercise)
    if (typeof updatedExerciseIndex !== "undefined") setUpdatedBlockIndex(updatedExerciseIndex)

    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    sidebarNavigateTo({
      content: <ExerciseFormInfos />,
      leftButtonText: word("program.planning.create.exercise.returnSession"),
      onHide: () => {
        return onHideBlockForm.current()
      },
    })
  }

  const navigateToNoteForCoachesForm = (
    isCreation: boolean,
    updatedNoteIndex: number | null,
    initialUpdatedBlock?: SessionBlockFormikType,
  ) => {
    setIsEditingBlock(!isCreation)
    if (initialUpdatedBlock) setInitialUpdatedBlock(initialUpdatedBlock)
    if (typeof updatedNoteIndex !== "undefined") setUpdatedBlockIndex(updatedNoteIndex)

    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    sidebarNavigateTo({
      content: <NoteForCoachesForm />,
      leftButtonText: word("program.planning.create.exercise.returnSession"),
      onHide: () => {
        return onHideBlockForm.current()
      },
    })
  }

  const navigateToNoteForAthletesForm = (
    isCreation: boolean,
    updatedNoteIndex: number | null,
    initialUpdatedBlock?: SessionBlockFormikType,
  ) => {
    setIsEditingBlock(!isCreation)
    if (initialUpdatedBlock) setInitialUpdatedBlock(initialUpdatedBlock)
    if (typeof updatedNoteIndex !== "undefined") setUpdatedBlockIndex(updatedNoteIndex)

    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    sidebarNavigateTo({
      content: <NoteForAthletesForm />,
      leftButtonText: word("program.planning.create.exercise.returnSession"),
      onHide: () => {
        return onHideBlockForm.current()
      },
    })
  }

  const navigateToMediaForm = (
    isCreation: boolean,
    updatedBlockIndex: number | null,
    initialUpdatedBlock?: SessionBlockFormikType,
  ) => {
    setIsEditingBlock(!isCreation)
    if (initialUpdatedBlock) setInitialUpdatedBlock(initialUpdatedBlock)
    if (typeof updatedBlockIndex !== "undefined") setUpdatedBlockIndex(updatedBlockIndex)

    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    sidebarNavigateTo({
      content: <MediaForm />,
      leftButtonText: word("program.planning.create.exercise.returnSession"),
      onHide: () => {
        return onHideBlockForm.current()
      },
    })
  }

  const navigateToChronoForm = () => {
    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })

    sidebarNavigateTo({
      content: <ChronoDetail />,
      leftButtonText: word("program.planning.create.exercise.returnExercise"),
      onHide: () => {
        return onHideChronoForm.current()
      },
    })
  }

  return {
    navigateToSessionView,
    navigateToSessionForm,
    navigateToExerciseForm,
    navigateToNoteForCoachesForm,
    navigateToNoteForAthletesForm,
    navigateToMediaForm,
    navigateToChronoForm,
    sidebarNavigateBack,
    isSidebarVisible,
  }
}
